import { formatCurrency, formatDate } from '@crm/shared';
import { Badge, Card, CardContent } from '@crm/ui';
import { BadgeCheck, CircleAlert } from 'lucide-react';
import { StatusHeader } from '@/components';

export interface PlanLimits {
  name: string;
  description: string | null;
  monthlyPrice: number;
  maxUnits: number | null;
  maxUsers: number | null;
  includedMessages: number | null;
}

export function planFeatures(plan: PlanLimits): string[] {
  return [
    plan.maxUnits === null ? 'Unidades sem limite' : `Ate ${plan.maxUnits} unidades`,
    plan.maxUsers === null ? 'Usuarios sem limite' : `Ate ${plan.maxUsers} usuarios`,
    plan.includedMessages === null
      ? 'Mensagens sem limite'
      : `${plan.includedMessages.toLocaleString('pt-BR')} mensagens no mes`,
  ];
}

export function planLimits(plan: PlanLimits): string {
  return planFeatures(plan).join(' - ');
}

interface PlanSummaryProps {
  plan: PlanLimits | null;
  startedAt: string | null;
  emptyDescription: string;
  children?: React.ReactNode;
}

export function PlanEmpty({ description }: { description: string }) {
  return (
    <StatusHeader
      icon={<CircleAlert className="h-5 w-5" />}
      tone="wait"
      title="Sem plano contratado"
      detail={description}
    />
  );
}

export function PlanSummary({ plan, startedAt, emptyDescription, children }: PlanSummaryProps) {
  if (!plan) {
    return (
      <Card variant="flush">
        <CardContent className="py-5">
          <PlanEmpty description={emptyDescription} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="flush">
      <CardContent className="space-y-4 py-5">
        <div className="flex flex-wrap items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <BadgeCheck className="h-5 w-5" />
          </span>

          <div className="min-w-0 flex-1 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="border-primary/50 text-primary">
                Plano atual
              </Badge>
              <span className="font-medium">{plan.name}</span>
            </div>

            {plan.description ? (
              <p className="text-sm text-muted-foreground">{plan.description}</p>
            ) : null}
          </div>

          <div className="shrink-0 text-right">
            <p className="text-2xl font-semibold tracking-tight">
              {formatCurrency(plan.monthlyPrice)}
            </p>
            <p className="text-xs text-muted-foreground">
              por mes{startedAt ? `, desde ${formatDate(startedAt)}` : ''}
            </p>
          </div>
        </div>

        {children ? <div className="border-t border-border pt-4">{children}</div> : null}
      </CardContent>
    </Card>
  );
}
